import React, { useEffect } from 'react';
import { Box, Typography, Paper,Tooltip } from '@mui/material';
import { format, addDays, startOfWeek, isSameDay } from 'date-fns';
import { calendarBooking } from '../../types/calendarBooking';
import { formatEventTime, formatEventTimeMonth, isToday, parseDateTime } from '../../services/calendarUtils';
import { TimezoneService } from '../../services/timezoneUtils';

interface WeekViewProps {
  currentDate: Date;
  events: calendarBooking[];
  onDateChange: (date: Date) => void;
  onEventClick?: (booking: calendarBooking) => void;
}

const WeekView: React.FC<WeekViewProps> = ({ 
  currentDate, 
  events, 
  onDateChange,
  onEventClick 
}) => {
  const start = startOfWeek(currentDate, { weekStartsOn: 1 });

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getEventsByDay = (day: Date) => {
  const dayEvents = events.filter(event => {
    if (!event.start?.dateTime) return false;
    const eventStart = parseDateTime(event.start);
    return eventStart && isSameDay(eventStart, day);
  });

  // Sort events by start time for the day
  return dayEvents.sort((a, b) => {
    if (!a?.start?.dateTime || !b?.start?.dateTime) return 0;
    
    try {
      const timeA = TimezoneService.convertBackendTimeToLocal(a.start.dateTime);
      const timeB = TimezoneService.convertBackendTimeToLocal(b.start.dateTime);
      
      // Compare by time (hours and minutes)
      const minutesA = timeA.getHours() * 60 + timeA.getMinutes();
      const minutesB = timeB.getHours() * 60 + timeB.getMinutes();
      
      return minutesA - minutesB;
    } catch (error) {
      console.error('Error sorting day events:', error);
      return 0;
    }
  });
};

  useEffect(() => {
    weekDays.forEach(day => {
      const dayEvents = getEventsByDay(day);
    });
  }, [events, currentDate]);

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <Box id="week-view">
        <Box sx={{ width: '60px', mt: '60px', borderRight: '1px solid #ddd' }}>
          {hours.map(hour => (
            <Box
              key={hour}
              sx={{
                height: '60px',
                borderTop: '1px solid #ddd',
                p: 0.5,
                textAlign: 'right',
                pr: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                {hour === 12 ? '12pm' : hour > 12 ? `${hour - 12}pm` : `${hour}am`}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexGrow: 1, overflowX: 'auto' }}>
          {weekDays.map((day, dayIndex) => {
            const dayEvents = getEventsByDay(day);
            
            return (
              <Box
                key={dayIndex}
                sx={{
                  flexGrow: 1,
                  minWidth: '120px',
                  width: '20%',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    height: '60px',
                    p: 1,
                    textAlign: 'center',
                    borderBottom: '1px solid #ddd',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: isToday(day) ? 'bold' : 'normal',
                      color: isToday(day) ? 'primary.main' : 'text.primary',
                    }}
                  >
                    {format(day, 'EEE')}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: isToday(day) ? 'bold' : 'normal',
                      color: isToday(day) ? 'primary.main' : 'text.primary',
                    }}
                  >
                    {format(day, 'd')}
                  </Typography>
                </Box>

                {/* Hour cells */}
                <Box
                  sx={{
                    bgcolor: isToday(day) ? 'rgba(224, 222, 222, 0.12)' : 'transparent',
                  }}
                >
                  {hours.map(hour => (
                    <Box
                      key={hour}
                      sx={{
                        height: '60px',
                        borderTop: '1px solid #ddd',
                        borderLeft: '1px solid #ddd',
                        '&:last-child': { borderBottom: '1px solid #ddd' },
                      }}
                    />
                  ))}
                </Box>

                <Box sx={{ position: 'absolute', top: '60px', left: 0, right: 0, zIndex: 2 }}>
                  {dayEvents.map((event, eventIndex) => {
                    const eventStart = parseDateTime(event.start);
                    const eventEnd = parseDateTime(event.end);
                    
                    if (!eventStart || !eventEnd) return null;
                    
                    // Calculate positioning
                    const startHour = eventStart.getHours();
                    const startMinutes = eventStart.getMinutes();
                    const top = startHour * 60 + startMinutes; // Position based on actual time
                    
                    const endHour = eventEnd.getHours();
                    const endMinutes = eventEnd.getMinutes();
                    const duration = (endHour * 60 + endMinutes) - (startHour * 60 + startMinutes);
                    
                    // Calculate event width based on overlapping events
                    const eventsAtSameTime = dayEvents.filter(e => {
                      const eStart = parseDateTime(e.start);
                      const eEnd = parseDateTime(e.end);
                      if (!eStart || !eEnd) return false;
                      
                      // Check if events overlap
                      return (eStart < eventEnd && eEnd > eventStart);
                    });
                    
                    const eventPosition = eventsAtSameTime.indexOf(event);
                    const totalOverlappingEvents = eventsAtSameTime.length;
                    const leftOffset = totalOverlappingEvents > 1 ? (100 / totalOverlappingEvents) * eventPosition : 0;
                    const eventWidth = totalOverlappingEvents > 1 ? 100 / totalOverlappingEvents : 100;

                    return (
                      <Tooltip title={<Box>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {event?.customerName}
                                </Typography><br/>
                                <Typography variant="body2">
                                  {formatEventTime(event)}
                                </Typography><br/>
                                {event?.serviceName && (
                                    <Typography variant="body2">
                                      {event.serviceName}
                                    </Typography>
                                )}
                              </Box>}
                              arrow placement="top">
                      <Paper
                        key={`${eventIndex}-${event.bookingId || eventIndex}`}
                        onClick={() => onEventClick && onEventClick(event)}
                        sx={{
                          position: 'absolute',
                          top: `${top}px`,
                          left: `${leftOffset}%`,
                          width: `${eventWidth}%`,
                          height: `${Math.max(duration, 30)}px`, // Minimum height of 30px
                          bgcolor: event?.color,
                          color: 'white',
                          p: 0.5,
                          overflow: 'hidden',
                          borderRadius: 1,
                          cursor: 'pointer', // Add this to show it's clickable
                          '&:hover': {
                            opacity: 0.9,
                            boxShadow: 2,
                          },
                        }}
                      >
                        
                        <Typography variant="caption" sx={{ fontSize: '0.8rem', display: 'block', marginLeft: 0 }}>
                          {event?.customerName}
                        </Typography>
                        {event?.serviceName && (
                          <Typography
                            variant="caption"
                            sx={{ fontSize: '0.7rem', opacity: 0.9, display:'block', marginLeft: 0 }}
                          >
                            {event?.serviceName}
                          </Typography>
                        )}
                      </Paper>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default WeekView;